import { useTranslation } from 'next-i18next'
import { numberWithCommas } from 'src/utils'
import { DEFAULT_PER_SQM_RATES, OnDemandCityConfig } from 'src/utils/onDemand'

import { DurationTierKey, getTierDiscountPct } from './OnDemandWizard/helpers'

/**
 * Three small editorial sections distilled from partner field-learnings
 * (Boxmi / Kanuby / Spakio): duration-tiered pricing, not-instant
 * scheduling, and the restricted-items list. Each section is intentionally
 * compact — they exist to set honest expectations BEFORE the lead form, so
 * partner-side qualification doesn't surprise the renter post-submit.
 *
 * Placement on the on-demand landing (composed in `index.tsx`):
 *  - `NotInstantStrip` after the 4-step process — anchors the "we schedule"
 *    expectation right where users learn the flow.
 *  - `DurationTiers` after `IndicativeSizesGrid` — adds the partner's
 *    duration-discount story to the existing flat-price tiles.
 *  - `RestrictedItems` after the FAQ, just above the form — last surface
 *    before commit so users with disqualifying inventory bail before
 *    spending sales-call time.
 */

type DurationTier = {
  DURATION: string
  UNIT: string
  NOTE: string
}

interface DurationTiersProps {
  /** Resolved city coverage (city pages). Prices come from its `perSqmRates`. */
  coverage?: OnDemandCityConfig
  /** Parent-page fallback — first active city's coverage. */
  fallbackCoverage?: OnDemandCityConfig
}

/**
 * The three duration tiers, in display order. This — not the locale array —
 * drives the rows: prices are NOT in the locale file, because rates are per
 * market (Monterrey → Boxmi, CDMX → Spakio) and a translation string cannot
 * vary by city, so a hardcoded `PRICE` silently showed Monterrey's card on
 * every market. Rates come from coverage instead, the same source as
 * `IndicativeSizesGrid` and the wizard's `PriceReveal`; the locale supplies
 * only the labels, matched positionally.
 */
const TIER_ORDER: readonly DurationTierKey[] = [
  'monthToMonth',
  'sixMonths',
  'twelveMonths'
]

const DurationTiers = ({ coverage, fallbackCoverage }: DurationTiersProps) => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')
  const tiers = t('DURATION_TIERS.TIERS', {
    returnObjects: true
  }) as DurationTier[]
  const rates =
    coverage?.perSqmRates ??
    fallbackCoverage?.perSqmRates ??
    DEFAULT_PER_SQM_RATES

  return (
    <section className="border-t border-ink-100/15 bg-white-500 py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="md:grid md:grid-cols-12 md:gap-12">
          <div className="md:col-span-4">
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {t('DURATION_TIERS.EYEBROW')}
            </p>
            <h2 className="mt-3 font-display text-[24px] leading-[1.2] font-medium tracking-[-0.01em] text-ink-700 md:text-[32px]">
              {t('DURATION_TIERS.TITLE')}
            </h2>
            <p className="mt-3 text-[15px] leading-relaxed text-ink-200 md:text-base">
              {t('DURATION_TIERS.SUBTITLE', {
                pct: getTierDiscountPct(rates, 'twelveMonths')
              })}
            </p>
          </div>

          <div className="mt-10 md:col-span-8 md:mt-0">
            <ul className="flex flex-col">
              {TIER_ORDER.map((tierKey, index) => {
                const tier = tiers[index]
                if (!tier) return null
                // Computed per market — the locale NOTE carries only the
                // descriptive half ("ideal para mudanzas"), never a "−N%".
                const discountPct = getTierDiscountPct(rates, tierKey)
                return (
                  <li
                    key={tierKey}
                    className={`flex flex-col gap-2 py-5 md:flex-row md:items-baseline md:justify-between md:gap-8 md:py-6 ${
                      index > 0 ? 'border-t border-ink-100/15' : ''
                    }`}
                  >
                    <div className="md:w-1/3">
                      <p className="font-display text-[18px] leading-tight font-medium text-ink-700 md:text-[20px]">
                        {tier.DURATION}
                      </p>
                    </div>
                    <div className="md:w-1/3">
                      <p className="font-display text-[28px] leading-none font-medium tracking-[-0.02em] text-ink-700 md:text-[34px]">
                        {`$${numberWithCommas(rates[tierKey])}`}
                      </p>
                      <p className="mt-1 text-[12px] tracking-wide text-ink-200 uppercase md:text-[13px]">
                        {tier.UNIT}
                      </p>
                    </div>
                    <p className="text-[14px] leading-relaxed text-ink-200 md:w-1/3 md:text-[15px]">
                      {discountPct > 0
                        ? `${t('DURATION_TIERS.DISCOUNT_VS_MONTH', { pct: discountPct })} · ${tier.NOTE}`
                        : tier.NOTE}
                    </p>
                  </li>
                )
              })}
            </ul>
            <p className="mt-6 text-[12px] leading-relaxed text-ink-200 italic md:text-[13px]">
              {t('DURATION_TIERS.FOOTNOTE')}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

const NotInstantStrip = () => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')
  return (
    <section className="border-t border-ink-100/15 bg-white-500 py-12 md:py-16">
      <div className="mx-auto max-w-5xl px-5 md:flex md:items-start md:gap-8 md:px-10">
        <div className="md:w-1/3">
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            {t('NOT_INSTANT.EYEBROW')}
          </p>
          <h2 className="mt-3 font-display text-[20px] leading-[1.25] font-medium tracking-[-0.01em] text-ink-700 md:text-[24px]">
            {t('NOT_INSTANT.TITLE')}
          </h2>
        </div>
        <p className="mt-3 text-[15px] leading-relaxed text-ink-200 md:mt-0 md:w-2/3 md:text-base">
          {t('NOT_INSTANT.BODY')}
        </p>
      </div>
    </section>
  )
}

const RestrictedItems = () => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')
  const items = t('RESTRICTED_ITEMS.ITEMS', {
    returnObjects: true
  }) as string[]
  return (
    <section className="border-t border-ink-100/15 bg-white-500 py-20 md:py-28">
      <div className="mx-auto max-w-5xl px-5 md:grid md:grid-cols-12 md:gap-12 md:px-10">
        <div className="md:col-span-5">
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            {t('RESTRICTED_ITEMS.EYEBROW')}
          </p>
          <h2 className="mt-3 font-display text-[22px] leading-[1.2] font-medium tracking-[-0.01em] text-ink-700 md:text-[28px]">
            {t('RESTRICTED_ITEMS.TITLE')}
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-ink-200 md:text-base">
            {t('RESTRICTED_ITEMS.INTRO')}
          </p>
        </div>
        <div className="mt-8 md:col-span-7 md:mt-0">
          <ul className="flex flex-col">
            {items.map((item, index) => (
              <li
                key={item}
                className={`flex items-start gap-3 py-3 text-[15px] leading-relaxed text-ink-700 md:text-base ${
                  index > 0 ? 'border-t border-ink-100/15' : ''
                }`}
              >
                <i
                  className="si si-circle-xmark mt-0.5 text-base text-blue-500"
                  aria-hidden="true"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-[12px] leading-relaxed text-ink-200 italic md:text-[13px]">
            {t('RESTRICTED_ITEMS.FOOTNOTE')}
          </p>
        </div>
      </div>
    </section>
  )
}

export { DurationTiers, NotInstantStrip, RestrictedItems }
